import { Checkbox } from "@msuite/ui/checkbox";
import { useTranslation } from "@msuite/ui/localization";
import { Tooltip } from "@msuite/ui/tooltip";
import { useFormContext, useWatch } from "react-hook-form";
import type { OffsetRow, PaymentOffsetsFormValues } from "./to-offset-rows";

/** Props Interface */
interface OffsetPickCellProps {
	row: OffsetRow;
}

/**
 * Pick an outgoing invoice, or put it back. A pick goes to the end of the order, which is the order
 * the offsets are taken off in; one planned against another open invoice cannot be picked.
 */
export function OffsetPickCell({ row }: OffsetPickCellProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const { control, setValue } = useFormContext<PaymentOffsetsFormValues>();
	const outgoingInvoiceIds = useWatch({ control, name: "outgoingInvoiceIds" });

	/** Functions */
	function handleOnChange(isPicked: boolean) {
		const next = outgoingInvoiceIds.filter((id) => id !== row.id);
		if (isPicked) next.push(row.id);
		setValue("outgoingInvoiceIds", next, { shouldDirty: true });
	}

	/** Render */
	return (
		<Tooltip
			label={t("incoming_invoices.payments.offsets.used_elsewhere", { number: row.usedBy ?? "" })}
			isDisabled={!row.usedBy}
		>
			<div className="h-stack gap-2">
				<Checkbox
					value={row.position !== null}
					onChange={handleOnChange}
					isDisabled={!!row.usedBy && row.position === null}
					data-cy="offset-candidate-checkbox"
				/>
				{row.position !== null && <span className="label tabular-nums">{row.position}.</span>}
			</div>
		</Tooltip>
	);
}
